import type { Command } from 'commander';
import { unwrapDataArray } from '@wport/core';
import { personalGet, type PersonalRequestOptions } from '../../lib/personal-client';
import { loadPersonalCredentials } from '../../lib/credentials-store';
import { resolveContext, type ResolvedContext } from '../../lib/global-opts';
import { printJson, sanitizeForTerminal } from '../../lib/output';
import { OAUTH_SESSIONS_BASE, SessionItem } from '@wport/core';

const PLACEHOLDER = '—';

/**
 * 先 sanitize 再判空:device_name 等欄位是伺服器回應內容的往返值(見 SessionItem 契約)，
 * 視同 untrusted 輸出處理——同一批資料稍後也會餵給 Task 6 的 `sessions list`(其他裝置名
 * 真的可被其他登入端任意設定),這裡先建立一致的防護慣例，不要等 Task 6 才補。
 * 先 sanitize 再檢查是否為空，避免「整串都是控制字元」的值繞過空值判斷、印出空白而非佔位符。
 */
function display(value: string | null | undefined): string {
	const clean = sanitizeForTerminal(value ?? '');
	return clean.length > 0 ? clean : PLACEHOLDER;
}

/**
 * `wport whoami` 核心：`GET /oauth/sessions` 找出 `is_current` 那筆，顯示裝置名/伺服器端 session
 * 時間戳 + 本機登入時間。
 *
 * v1 沒有身分(display_name/email)可顯示 —— AS 的 TokenResponse 不含身分資訊，profile 端點待
 * spec B 落地(見 login.ts 大段註解);這裡改用 sessions API 的裝置資訊當替代身分佐證。
 *
 * 未登入時不重複判斷 —— `personalGet` 本身在 credentials 缺席時就會丟 CliError exit 3
 * (訊息含 `wport login`),這裡讓錯誤直接上拋給 top-level handler,避免與 personal-client
 * 的登入態檢查邏輯重複一份。
 */
export async function performWhoami(ctx: ResolvedContext): Promise<void> {
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const { body } = await personalGet(opts, OAUTH_SESSIONS_BASE);
	const sessions = unwrapDataArray<SessionItem>(body);
	const current = sessions.find((s) => s.is_current);
	// 呼叫到這裡代表 personalGet 剛才已用本機 personal 憑證通過認證，local 區必然存在。
	const localLoginAt = loadPersonalCredentials()?.session_created_at ?? null;

	if (ctx.format === 'json') {
		printJson({
			logged_in: true,
			device_name: current?.device_name ?? null,
			session_created_at: current?.created_at ?? null,
			last_used_at: current?.last_used_at ?? null,
			local_login_at: localLoginAt,
		});
		return;
	}

	const lines = [
		'Logged in.',
		`device:            ${display(current?.device_name)}`,
		`session created:   ${display(current?.created_at)}`,
		`last used:         ${display(current?.last_used_at)}`,
		`local login time:  ${display(localLoginAt)}`,
	];
	process.stdout.write(lines.join('\n') + '\n');
}

export function registerWhoamiCommand(program: Command): void {
	program
		.command('whoami')
		.description('Show your current personal login session (contacts the server)')
		.action(async (_flags: unknown, command: Command) => {
			await performWhoami(resolveContext(command));
		});
}
